<template>
    <v-row
        no-gutters
        class="mb-4"
    >
        <v-col>
            <v-dialog
                v-if="editModal.open"
                v-model="editModal.open"
                :width="500">
                <AdminCompanyDialog
                    :payload="editModal.payload"
                    @close="onCloseClick"
                >
                </AdminCompanyDialog>
            </v-dialog>

            <v-row no-gutters>
                <v-col class="mb-2">
                    <NewFilterBar
                        :available-filters="filterHelper.getAvailableFilters()"
                        :before-route-change="filterHelper.setFilterParamsIfEmpty.bind(filterHelper)"
                        @route_change="debouncedBackendQuery"
                    >
                    </NewFilterBar>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-toolbar flat class="medium-toolbar" color="transparent">
                        <v-toolbar-title class="font-weight-bold mb-3">
                            {{ total_count }}
                            {{ $tc("row | rows", total_count) }}
                        </v-toolbar-title>
                    </v-toolbar>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    &nbsp;&nbsp;
                    MRR: <strong>{{ stats.mrr }} €</strong>
                    &nbsp;&nbsp;
                    Paid users: <strong>{{ stats.paid_users }}</strong>
                    &nbsp;&nbsp;
                    Companies: <strong>{{ stats.companies }}</strong>
                    &nbsp;&nbsp;
                    Countries: <strong>{{ stats.countries }}</strong>
                    &nbsp;&nbsp;
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <icon-button :on="on" @click="copyToClipboard(stats.tooltip)">
                                mdi-content-copy
                            </icon-button>
                        </template>
                        {{ stats.tooltip }}
                    </v-tooltip>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :options="filterHelper.getTableOptions()"
                        :server-items-length="total_count"
                        :footer-props="table_footer_props"
                        hide-default-header
                        class="dense-table overflow-disable"
                        @update:options="filterHelper.setTableOptions($event)"
                    >
                        <template v-slot:header="{ props, on }">
                            <thead class="v-data-table-header">
                            <tr>
                                <th
                                    v-for="(header, index) in props['headers']"
                                    :key="index"
                                    :class="[formatTableHeaderClass(header, props['options'])]"
                                    :width="header.width"
                                    role="columnheader"
                                    style="white-space: nowrap; flex-wrap: nowrap;"
                                    scope="col"
                                    v-on="on"
                                    @click="header.sortable !== false && header['value'] !== 'data-table-select'
                                                ? filterHelper.changeSort(header.value)
                                                : ''">
                                        <span class="subtitle-2">
                                            {{ header.text }}
                                        </span>
                                    <v-icon v-if="header.value === filterHelper.getUrlParam('sortBy')">
                                        {{ filterHelper.getUrlParam('sortDesc') ? "mdi-menu-down" : "mdi-menu-up" }}
                                    </v-icon>
                                </th>
                            </tr>
                            </thead>
                        </template>

                        <template v-slot:item.braintree_customer_id="{ item }">
                            <div class="text-right">
                                <v-btn icon text color="n3" class="mr-2"
                                       :hidden="!item.braintree_customer_id"
                                       @click="onBraintreeClick(item.braintree_customer_id)">
                                    <v-icon>web</v-icon>
                                </v-btn>
                            </div>
                        </template>

                        <template v-slot:item.intercom_url="{ item }">
                            <div class="text-right">
                                <v-btn icon text color="n3" class="mr-2"
                                       :hidden="!item.intercom_url"
                                       @click="onUrlClick(item.intercom_url)">
                                    <v-icon>web</v-icon>
                                </v-btn>
                            </div>
                        </template>

                        <template v-slot:item.edit="{ item }">
                            <div class="text-right">
                                <v-btn
                                    icon
                                    text
                                    color="n3"
                                    class="mr-2"
                                    @click="onEditClick(item)"
                                >
                                    <v-icon>edit</v-icon>
                                </v-btn>
                            </div>
                        </template>

                        <template v-slot:item.support="{ item }">
                            <div class="text-right">
                                <v-btn
                                    depressed
                                    small
                                    color="primary"
                                    class="inverted--text"
                                    @click="onAbilineClick(item.company_id)"
                                >
                                    {{ ("Support") }}
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import Vue from "vue"
import NewFilterBar from "../../components/NewFilterBar"
import {getFilterHelper} from "@/components/NewFilterBar/FilterBar"
import AddFilterButton from "@/components/NewFilterBar/components/AddFilterButton"
import {reactive, ref, toRefs} from "@vue/composition-api"
import AccountModule from "../../store/modules/account"
import {useTable} from "@/composables/use-table"
import AdminMemberDialog from "./AdminMemberDialog.vue"
import {debounce} from "throttle-debounce"
import errorDialog from "@/views/ErrorDialog/errorDialog"
import * as IntercomModule from "../../modules/intercom/intercom"
import AdminCompanyDialog from "./AdminCompanyDialog.vue";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "AdminCompany",
    components: {AdminCompanyDialog: AdminCompanyDialog, NewFilterBar, AdminMemberDialog, AddFilterButton},
    setup() {
        const accountModule = AccountModule()
        const {table_footer_props, formatTableHeaderClass} = useTable(["created_at"], true)
        const items = ref([])
        const total_count = ref(0)
        const stats = ref({})
        const filterHelper = getFilterHelper({
            filters: [
                {key: "company_search", value: [], default: true},
                {key: "user_search", value: [], default: true},
                {key: "company_status", value: ["active"], default: true},
                {key: "payment_gateway", value: [], default: true},
                {key: "plan_status", value: [], default: true},
                {key: "plan_period", value: [], default: true},
            ],
            table_parameters: [
                {key: "sortBy", value: "company_id"},
                {key: "sortDesc", value: true},
                {key: "itemsPerPage", value: 25},
                {key: "page", value: 1},
            ]
        })
        filterHelper.setFilterParamsIfEmpty()

        const state = reactive({
            namespace: "admin_members",
            headers: [
                {text: "", value: "edit", sortable: false},
                {text: "", value: "support", sortable: false},
                {text: ("Company"), value: "company_id",},
                {text: ("Name"), value: "name"},
                {text: ("Status"), value: "status"},
                {text: ("Last seen"), value: "last_seen_at"},
                {text: ("Plan"), value: "plan_name"},
                {text: ("Plan until"), value: "plan_until"},
                {text: ("MRR"), value: "plan_spend"},
                {text: ("Plan paid users"), value: "paid_users_count"},
                {text: ("Plan max users"), value: "max_users_count"},
                {text: ("Plan price"), value: "plan_price"},
                {text: ("Plan period"), value: "plan_period"},
                {text: ("Payment gateway"), value: "payment_gateway"},
                {text: ("Active users"), value: "active_users_count"},

                {text: ("Trial"), value: "is_trial"},
                {text: ("Paid"), value: "is_paid"},
                {text: ("Churn"), value: "is_churn"},

                {text: ("Region"), value: "region"},
                {text: ("Country"), value: "country"},
                {text: ("Industry"), value: "industry"},

                {text: ("Intercom"), value: "intercom_url"},
                {text: ("Braintree"), value: "braintree_customer_id"},

                {text: ("Owner"), value: "owner_name"},
                {text: ("Company at"), value: "company_at", width: "120"},
                {text: ("First paid at"), value: "first_paid_at", width: "120"},
                {text: ("Cancelled at"), value: "cancelled_at", width: "120"},
                {text: ("UID"), value: "user_id"},
            ],
        })
        const {showFeedbackNotification} = FeedbackNotificationModule();
        const copyToClipboard = text => {
            navigator.clipboard.writeText(text)
            showFeedbackNotification('Copied to clipboard')
        }
        const backendQuery = () => {
            accountModule
                .fetchAdminData('company', filterHelper.getOnlyRelevantQueryParams())
                .then(function (resp) {
                    items.value = resp.items
                    total_count.value = resp.total_count
                    stats.value = resp.stats
                })
                .catch(function (ex) {
                    errorDialog.showError(ex)
                })
        }
        const debouncedBackendQuery = debounce(500, false, () => {
            backendQuery()
        })

        const onAbilineClick = company_id => {
            IntercomModule.shutdownIntercom()
            window.open("/abiline/?cid=" + company_id, "_self");
        };
        const onBraintreeClick = function (braintree_customer_id) {
            let url = 'https://www.braintreegateway.com/merchants/m8sq288gg3dqh7dz/customers/' + braintree_customer_id
            window.open(url, '_blank')
        }
        const onUrlClick = function (url) {
            window.open(url, '_blank')
        }

        const editModal = reactive({open: false, payload: {}})
        const onEditClick = function (payload) {
            editModal.payload = payload
            editModal.open = true
        }
        const onCloseClick = function (payload) {
            editModal.payload = null
            editModal.open = false
            backendQuery()
        }

        return {
            ...toRefs(state),
            debouncedBackendQuery,
            table_footer_props,
            formatTableHeaderClass,
            items,
            total_count,
            stats,
            onAbilineClick,
            editModal,
            onEditClick,
            onCloseClick,
            filterHelper,
            onUrlClick,
            onBraintreeClick,
            copyToClipboard,
        }
    }
}
</script>
